import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { NgbHighlight } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  catchError,
  combineLatest,
  defer,
  finalize,
  forkJoin,
  map,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';

import { SUPPORTED_CURRENCY_CODES } from 'src/app/core/constants/currency-options.constant';
import { CoreModule } from 'src/app/core/core.module';
import { INVENTORY_ITEM_TYPE_SERVICE } from 'src/app/core/enums/inventory-item-type.enum';
import { InventoryItemAvailability } from 'src/app/core/models/inventory/inventory-item-availability.interface';
import { InventoryItem } from 'src/app/core/models/inventory/inventory-item.interface';
import { InventoryItemComponent as InventoryItemComponentModel } from 'src/app/core/models/inventory/inventory-item-component.model';
import { UnitOfMeasureLookup } from 'src/app/core/models/inventory/unit-of-measure-lookup.interface';
import { CompanySalesOrderDetails } from 'src/app/core/models/sales/company-sales-order-details.interface';
import { CompanySalesOrderLine } from 'src/app/core/models/sales/company-sales-order-line.interface';
import { CompanySalesOrderLineUpsert } from 'src/app/core/models/sales/company-sales-order-line-upsert.interface';
import { SalesOrderInsufficientStockLine } from 'src/app/core/models/sales/sales-order-insufficient-stock.interface';
import { SalesOrderLineFormModel } from 'src/app/core/models/sales/sales-order-line-form.model';
import {
  SalesOrderLineCompositeChildView,
  SalesOrderLineCompositeView,
  SalesOrderLineView,
} from 'src/app/core/models/sales/sales-order-line-view.interface';
import { InventoryItemService } from 'src/app/core/services/inventory-item.service';
import { SalesOrderService } from 'src/app/core/services/sales-order.service';
import {
  inventoryItemOptionCompare,
  inventoryItemOptionFilter,
  inventoryItemOptionFormatter,
} from 'src/app/core/utils/inventory-item-search.util';
import {
  SalesOrderInventoryItemOption,
  SalesOrderLineAvailabilityView,
  SalesOrderLineTotalsPreview,
  SalesOrderLineUnitOption,
} from '../sales-order-overview.models';

@Component({
  selector: 'app-sales-order-lines-tab',
  standalone: true,
  imports: [CommonModule, CoreModule, NgbHighlight, ReactiveFormsModule, TranslateModule],
  providers: [SalesOrderLineFormModel],
  templateUrl: './sales-order-lines-tab.component.html',
  styleUrl: './sales-order-lines-tab.component.scss',
})
export class SalesOrderLinesTabComponent implements OnChanges, OnDestroy {
  readonly lineFormModel = inject(SalesOrderLineFormModel);
  private readonly salesOrderService = inject(SalesOrderService);
  private readonly inventoryItemService = inject(InventoryItemService);

  @Input({ required: true }) public companyId = '';
  @Input({ required: true }) public salesOrderId = '';
  @Input({ required: true }) public salesOrder: CompanySalesOrderDetails | null = null;
  @Input({ required: true }) public unitsOfMeasure: UnitOfMeasureLookup[] = [];
  @Input({ required: true }) public inventoryItemOptions: SalesOrderInventoryItemOption[] = [];
  @Input() public isReadonly = false;
  @Input() public insufficientStockLines: SalesOrderInsufficientStockLine[] = [];

  @Output() public readonly orderUpdated = new EventEmitter<CompanySalesOrderDetails>();

  public readonly linesView$ = new BehaviorSubject<SalesOrderLineView[]>([]);
  public readonly lineUnitOptions$ = new BehaviorSubject<SalesOrderLineUnitOption[]>([]);
  public readonly lineAvailabilityView$ = new BehaviorSubject<SalesOrderLineAvailabilityView | null>(null);
  // True when the inventory item currently selected in the inline edit row is a Service.
  // Drives the "Always available" badge that replaces the numeric stock cells while editing.
  // Recomputed in `applySelectedInventoryItem` from `availability.isServiceItem` so the
  // template binds only to plain fields (AGENTS Angular template rule).
  public readonly isEditingServiceItem$ = new BehaviorSubject<boolean>(false);
  public readonly linePreview$: Observable<SalesOrderLineTotalsPreview> = defer(() =>
    combineLatest([
      this.lineForm.valueChanges.pipe(startWith(this.lineForm.getRawValue())),
      this.lineUnitOptions$,
    ]),
  ).pipe(map(() => this.computeLinePreview()));

  public readonly inventoryItemFilterFn = inventoryItemOptionFilter;
  public readonly inventoryItemFormatter = inventoryItemOptionFormatter;
  public readonly inventoryItemCompareFn = inventoryItemOptionCompare;

  public isCreatingLine = false;
  public isSavingLine = false;
  public isDeletingLineId = '';
  public isLoadingItemDetails = false;
  public editingLineId = '';
  public lineModalErrorKey = '';

  // Expand/collapse state for composite line rows. Keyed by SalesOrderLine.id so that
  // two lines pointing at the same composite item can be expanded independently. The
  // resolved children are cached on the parent inventory-item id since they don't depend
  // on the line itself. Mutations of any of these fields must end in a `refreshLinesView()`
  // call so the precomputed `composite` view-model on each SalesOrderLineView stays in sync
  // — the template only ever binds to plain fields.
  private readonly expandedLineIds = new Set<string>();
  private readonly compositeComponentsByItemId = new Map<string, InventoryItemComponentModel[]>();
  private readonly compositeLoadingItemIds = new Set<string>();
  private readonly compositeErrorItemIds = new Set<string>();

  // View-model fields for the inline create row's static cells (Type badge + SKU).
  // Updated whenever the inventory-item picker selection changes.
  public createTypeBadge: string | null = null;
  public createSku = '—';
  // Read-only "Currency" options exposed to the inline edit row's <select>. Sources the
  // canonical UAH/EUR/USD set from the shared SUPPORTED_CURRENCY_CODES constant so a
  // future addition only needs one edit. Bound as a field so the template never resolves
  // an enum/constant import from its binding context (AGENTS Angular template rule).
  public readonly currencyOptions: readonly string[] = SUPPORTED_CURRENCY_CODES;

  private readonly unsubscribe$ = new Subject<void>();

  constructor() {
    this.lineForm.controls.inventoryItemId.valueChanges
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap((itemId) => this.handleInventoryItemSelection(itemId)),
      )
      .subscribe();
  }

  public get lineForm() {
    return this.lineFormModel.form;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['salesOrder'] ||
      changes['insufficientStockLines'] ||
      changes['inventoryItemOptions']
    ) {
      this.pruneExpansionStateForCurrentOrder();
      this.refreshLinesView();
    }

    if (changes['unitsOfMeasure']) {
      this.refreshLineUnitOptions(null);
    }
  }

  private refreshLinesView(): void {
    const order = this.salesOrder;
    if (!order) {
      this.linesView$.next([]);
      return;
    }

    const shortfallByLineId = new Map(
      this.insufficientStockLines.map((entry) => [entry.lineId, entry] as const),
    );
    const optionByItemId = new Map(
      this.inventoryItemOptions.map((option) => [option.id, option] as const),
    );
    this.linesView$.next(
      order.lines.map((line) => this.mapLine(line, shortfallByLineId.get(line.id) ?? null, optionByItemId)),
    );
  }

  // When the order changes (lines deleted, order reloaded after save), drop expanded entries
  // for line ids that no longer exist. The component-fetch cache stays keyed by inventory-item
  // id so it survives line edits and benefits siblings that share the same kit.
  private pruneExpansionStateForCurrentOrder(): void {
    const liveLineIds = new Set((this.salesOrder?.lines ?? []).map((line) => line.id));
    for (const lineId of [...this.expandedLineIds]) {
      if (!liveLineIds.has(lineId)) {
        this.expandedLineIds.delete(lineId);
      }
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public startCreateLine(): void {
    if (this.isReadonly || this.isCreatingLine || this.editingLineId) {
      return;
    }
    this.editingLineId = '';
    this.lineModalErrorKey = '';
    this.resetSelectedInventoryItem();
    this.lineForm.reset({
      inventoryItemId: null,
      description: '',
      description2: '',
      quantity: 1,
      unitOfMeasureId: this.unitsOfMeasure[0]?.id ?? null,
      unitPrice: 0,
      taxPercent: 0,
      currencyCode: null,
    });
    this.isCreatingLine = true;
  }

  public startEditLine(lineId: string): void {
    if (this.isReadonly || this.isCreatingLine || this.editingLineId) {
      return;
    }

    const order = this.salesOrder;
    if (!order) {
      return;
    }

    const line = order.lines.find((candidate) => candidate.id === lineId);
    if (!line) {
      return;
    }

    this.editingLineId = lineId;
    this.lineModalErrorKey = '';
    this.resetSelectedInventoryItem();
    this.lineForm.reset({
      inventoryItemId: line.inventoryItemId ?? null,
      description: line.description ?? '',
      description2: line.description2 ?? '',
      quantity: line.quantity,
      unitOfMeasureId: line.unitOfMeasureId ?? null,
      unitPrice: line.unitPrice,
      taxPercent: line.taxPercent,
      currencyCode: line.currencyCode ?? null,
    });
  }

  public cancelEditing(): void {
    if (this.isSavingLine) {
      return;
    }
    this.isCreatingLine = false;
    this.editingLineId = '';
    this.lineModalErrorKey = '';
    this.resetSelectedInventoryItem();
  }

  private endLineEditing(): void {
    this.isCreatingLine = false;
    this.editingLineId = '';
    this.lineModalErrorKey = '';
    this.resetSelectedInventoryItem();
  }

  public saveLine(): void {
    if (!this.companyId || !this.salesOrderId || this.isSavingLine || this.lineForm.invalid) {
      this.lineForm.markAllAsTouched();
      return;
    }

    const payload: CompanySalesOrderLineUpsert = {
      inventoryItemId: this.lineForm.controls.inventoryItemId.value,
      description: this.normalizeOptionalText(this.lineForm.controls.description.value),
      description2: this.normalizeOptionalText(this.lineForm.controls.description2.value),
      quantity: this.lineForm.controls.quantity.value,
      unitOfMeasureId: this.lineForm.controls.unitOfMeasureId.value ?? 0,
      unitPrice: this.lineForm.controls.unitPrice.value,
      taxPercent: this.lineForm.controls.taxPercent.value,
      // Trim + normalize: empty string becomes null so the backend falls back to the item's
      // base-UoM price currency. Non-empty values pass through verbatim — the backend
      // validates case-sensitive UAH/EUR/USD and rejects others with HTTP 400.
      currencyCode: this.normalizeOptionalText(this.lineForm.controls.currencyCode.value),
    };

    this.isSavingLine = true;
    this.lineModalErrorKey = '';

    const request$ = this.editingLineId
      ? this.salesOrderService.updateSalesOrderLine(this.companyId, this.salesOrderId, this.editingLineId, payload)
      : this.salesOrderService.createSalesOrderLine(this.companyId, this.salesOrderId, payload);

    request$
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((order) => {
          this.orderUpdated.emit(order);
          this.endLineEditing();
        }),
        catchError(() => {
          this.lineModalErrorKey = 'COMPONENTS.SALES_ORDERS.ERRORS.LINE_SAVE_FAILED';
          return EMPTY;
        }),
        finalize(() => {
          this.isSavingLine = false;
        }),
      )
      .subscribe();
  }

  public toggleCompositeExpansion(line: SalesOrderLineView): void {
    if (!line.isComposite || !line.inventoryItemId) {
      return;
    }

    if (this.expandedLineIds.has(line.id)) {
      this.expandedLineIds.delete(line.id);
      this.refreshLinesView();
      return;
    }

    this.expandedLineIds.add(line.id);
    this.refreshLinesView();
    this.loadCompositeComponents(line.inventoryItemId);
  }

  private loadCompositeComponents(itemId: string): void {
    if (!this.companyId || !itemId) {
      return;
    }
    if (this.compositeComponentsByItemId.has(itemId) || this.compositeLoadingItemIds.has(itemId)) {
      return;
    }

    this.compositeLoadingItemIds.add(itemId);
    this.compositeErrorItemIds.delete(itemId);
    this.refreshLinesView();

    this.inventoryItemService
      .getComponents(this.companyId, itemId)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((components) => {
          this.compositeComponentsByItemId.set(itemId, components);
        }),
        catchError(() => {
          this.compositeErrorItemIds.add(itemId);
          return EMPTY;
        }),
        finalize(() => {
          this.compositeLoadingItemIds.delete(itemId);
          this.refreshLinesView();
        }),
      )
      .subscribe();
  }

  public deleteLine(lineId: string): void {
    if (!this.companyId || !this.salesOrderId || !lineId || this.isDeletingLineId) {
      return;
    }

    this.isDeletingLineId = lineId;
    this.lineModalErrorKey = '';

    this.salesOrderService
      .deleteSalesOrderLine(this.companyId, this.salesOrderId, lineId)
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap(() => this.salesOrderService.getSalesOrder(this.companyId, this.salesOrderId)),
        tap((order) => this.orderUpdated.emit(order)),
        catchError(() => {
          this.lineModalErrorKey = 'COMPONENTS.SALES_ORDERS.ERRORS.LINE_DELETE_FAILED';
          return EMPTY;
        }),
        finalize(() => {
          this.isDeletingLineId = '';
        }),
      )
      .subscribe();
  }

  private mapLine(
    line: CompanySalesOrderLine,
    shortfall: SalesOrderInsufficientStockLine | null,
    optionByItemId: Map<string, SalesOrderInventoryItemOption>,
  ): SalesOrderLineView {
    const inventoryOption = line.inventoryItemId
      ? optionByItemId.get(line.inventoryItemId) ?? null
      : null;
    const shippedQuantity = Number(line.shippedQuantity ?? 0);
    const safeShipped = Number.isFinite(shippedQuantity) && shippedQuantity > 0 ? shippedQuantity : 0;
    const remaining = Math.max(0, line.quantity - safeShipped);
    const showRemainingBadge = safeShipped > 0 && safeShipped < line.quantity;
    const invoicedQuantity = Number(line.invoicedQuantity ?? 0);
    const safeInvoiced =
      Number.isFinite(invoicedQuantity) && invoicedQuantity > 0 ? invoicedQuantity : 0;
    const invoiceableRemaining = Math.max(0, safeShipped - safeInvoiced);
    const showInvoiceableBadge = safeInvoiced > 0 && safeInvoiced < safeShipped;
    const isComposite = inventoryOption?.isComposite === true;
    const isServiceLine = inventoryOption?.itemType === INVENTORY_ITEM_TYPE_SERVICE;
    return {
      id: line.id,
      inventoryItemId: line.inventoryItemId ?? null,
      isComposite,
      isServiceLine,
      description: line.description ?? '',
      description2: line.description2 ?? '',
      typeBadgeKey: this.resolveLineTypeBadgeKey(line.inventoryItemId, inventoryOption),
      sku: inventoryOption?.number ?? '—',
      quantity: line.quantity,
      shippedQuantity: safeShipped,
      invoicedQuantity: safeInvoiced,
      remaining,
      showRemainingBadge,
      invoiceableRemaining,
      showInvoiceableBadge,
      unitLabel: line.unitOfMeasureDisplayName || line.unitOfMeasureCode || '—',
      unitPrice: line.unitPrice,
      taxPercent: line.taxPercent,
      // Currency is now authoritative on the line itself. Convert null → '' so the
      // template's truthy guard hides the suffix when the backend has no code yet
      // (e.g. legacy lines whose item lacked a base-UoM price row at migration time).
      // NOTE: not routed through `normalizeOptionalText` because the helper returns
      // `string | null` (matching the upsert-payload contract), whereas this view-model
      // field is typed `string` and asserted to be `''` for missing values by the specs.
      currencyCode: line.currencyCode?.trim() ?? '',
      discount: 0,
      amountExcludingTax: line.amountExcludingTax,
      totalTaxAmount: line.totalTaxAmount,
      amountIncludingTax: line.amountIncludingTax,
      fulfillmentLabel: '—',
      shortfall: shortfall
        ? {
            requestedBaseQuantity: shortfall.requestedBaseQuantity,
            availableBaseQuantity: shortfall.availableBaseQuantity,
            components: shortfall.components ?? null,
          }
        : null,
      composite: this.buildCompositeView(line.id, line.inventoryItemId ?? null, isComposite, optionByItemId),
    };
  }

  // Precomputes the composite expand/collapse view-model for one line so the template binds
  // only to plain fields. Recomputed by `refreshLinesView()` on every state change so the
  // template doesn't have to call helpers per change-detection cycle (AGENTS Angular rule).
  private buildCompositeView(
    lineId: string,
    inventoryItemId: string | null,
    isComposite: boolean,
    optionByItemId: Map<string, SalesOrderInventoryItemOption>,
  ): SalesOrderLineCompositeView | null {
    if (!isComposite || !inventoryItemId) {
      return null;
    }
    const isExpanded = this.expandedLineIds.has(lineId);
    const toggleAriaLabelKey = isExpanded
      ? 'COMPONENTS.SALES_ORDERS.LINES.COLLAPSE_COMPONENTS'
      : 'COMPONENTS.SALES_ORDERS.LINES.EXPAND_COMPONENTS';

    if (!isExpanded) {
      return { isExpanded: false, toggleAriaLabelKey, state: 'collapsed', children: [] };
    }
    if (this.compositeLoadingItemIds.has(inventoryItemId)) {
      return { isExpanded: true, toggleAriaLabelKey, state: 'loading', children: [] };
    }
    if (this.compositeErrorItemIds.has(inventoryItemId)) {
      return { isExpanded: true, toggleAriaLabelKey, state: 'error', children: [] };
    }
    const cached = this.compositeComponentsByItemId.get(inventoryItemId);
    if (!cached) {
      // Expanded but the components fetch has never been triggered (e.g. companyId was
      // momentarily empty when toggleCompositeExpansion ran, so loadCompositeComponents
      // short-circuited before adding the item to compositeLoadingItemIds). The template
      // renders 'idle' identically to 'loading' so user-visible behavior is unchanged; the
      // distinct member exists so this edge can be observed separately in telemetry.
      return { isExpanded: true, toggleAriaLabelKey, state: 'idle', children: [] };
    }
    if (cached.length === 0) {
      return { isExpanded: true, toggleAriaLabelKey, state: 'empty', children: [] };
    }
    return {
      isExpanded: true,
      toggleAriaLabelKey,
      state: 'ready',
      children: cached.map((component) => this.buildCompositeChildView(component, optionByItemId)),
    };
  }

  private buildCompositeChildView(
    component: InventoryItemComponentModel,
    optionByItemId: Map<string, SalesOrderInventoryItemOption>,
  ): SalesOrderLineCompositeChildView {
    const childOption = optionByItemId.get(component.componentInventoryItemId) ?? null;
    const rawPrice = childOption?.unitPrice;
    const hasPrice = typeof rawPrice === 'number' && Number.isFinite(rawPrice);
    const unitPrice = hasPrice ? (rawPrice as number) : 0;
    return {
      componentId: component.id,
      childInventoryItemId: component.componentInventoryItemId,
      displayName: component.componentDisplayName,
      sku: component.componentNumber || '—',
      quantity: component.quantity,
      unitCode: component.componentBaseUnitOfMeasureCode || '—',
      hasPrice,
      unitPrice,
      lineTotal: hasPrice ? unitPrice * component.quantity : 0,
      currencyCode: childOption?.currencyCode?.trim() ?? '',
    };
  }


  private normalizeOptionalText(value: string | null): string | null {
    const normalized = value?.trim() ?? '';
    return normalized.length === 0 ? null : normalized;
  }

  private handleInventoryItemSelection(itemId: string | null | undefined): Observable<unknown> {
    if (!itemId) {
      this.resetSelectedInventoryItem();
      return EMPTY;
    }

    this.isLoadingItemDetails = true;

    return forkJoin({
      item: this.inventoryItemService.getInventoryItem(this.companyId, itemId),
      availability: this.inventoryItemService.getInventoryItemAvailability(this.companyId, itemId),
    }).pipe(
      tap(({ item, availability }) => this.applySelectedInventoryItem(item, availability)),
      catchError(() => {
        this.lineModalErrorKey = 'COMPONENTS.SALES_ORDERS.ERRORS.ITEM_LOAD_FAILED';
        this.resetSelectedInventoryItem();
        return EMPTY;
      }),
      finalize(() => {
        this.isLoadingItemDetails = false;
      }),
    );
  }

  private applySelectedInventoryItem(item: InventoryItem, availability: InventoryItemAvailability): void {
    // `availability.isServiceItem` is the authoritative signal from the backend — Service
    // items bypass the stock pipeline server-side. The controller-facing `/availability`
    // endpoint returns the raw `stock - reserved` projection (typically 0 for Service
    // items); the `decimal.MaxValue` sentinel lives on the internal reservation path only.
    // When true, keep the availability view null so the template never renders the numeric
    // stock cells / quantity-vs-available validator hint.
    const isServiceItem = availability.isServiceItem === true;
    this.lineAvailabilityView$.next(
      isServiceItem
        ? null
        : {
            stockBaseQuantity: availability.stockBaseQuantity,
            reservedBaseQuantity: availability.reservedBaseQuantity,
            availableBaseQuantity: availability.availableBaseQuantity,
            baseUnitOfMeasureName: availability.baseUnitOfMeasureName,
          },
    );
    this.isEditingServiceItem$.next(isServiceItem);

    this.createTypeBadge = isServiceItem
      ? 'COMPONENTS.SALES_ORDERS.LINES.SERVICE_BADGE'
      : 'COMPONENTS.SALES_ORDERS.LINES.PRODUCT_BADGE';
    this.createSku = item.number?.trim() || '—';

    // Currency pre-fill rule: only seed the dropdown from the picked item's base-UoM
    // currency when the user hasn't set one yet. This preserves an explicit override
    // when the user later swaps the inventory item.
    const suggestedCurrency = item.currencyCode?.trim() ?? '';
    if (suggestedCurrency.length > 0 && !this.normalizeOptionalText(this.lineForm.controls.currencyCode.value)) {
      this.lineForm.controls.currencyCode.setValue(suggestedCurrency);
    }

    this.refreshLineUnitOptions(item);

    const currentUnitId = this.lineForm.controls.unitOfMeasureId.value;
    const validUnit = this.lineUnitOptions$.value.some((option) => option.unitOfMeasureId === currentUnitId);
    if (!validUnit) {
      const baseUnit = this.lineUnitOptions$.value.find((option) => option.isBase);
      const fallback = baseUnit ?? this.lineUnitOptions$.value[0];
      this.lineForm.controls.unitOfMeasureId.setValue(fallback?.unitOfMeasureId ?? null);
    }

    if (!this.normalizeOptionalText(this.lineForm.controls.description.value)) {
      const itemTitle = item.displayName?.trim() || item.number?.trim() || '';
      if (itemTitle.length > 0) {
        this.lineForm.controls.description.setValue(itemTitle);
      }
    }

    if (this.lineForm.controls.unitPrice.value === 0 && item.unitPrice > 0) {
      this.lineForm.controls.unitPrice.setValue(item.unitPrice);
    }
  }

  private resetSelectedInventoryItem(): void {
    this.lineAvailabilityView$.next(null);
    this.isEditingServiceItem$.next(false);
    this.createTypeBadge = null;
    this.createSku = '—';
    this.refreshLineUnitOptions(null);
  }

  /**
   * Resolves the per-row type badge i18n key. Lines that target a Service inventory item
   * render the Service badge; product/hybrid lines (and free-text lines without an item)
   * keep the existing Product badge / em-dash behavior.
   */
  private resolveLineTypeBadgeKey(
    inventoryItemId: string | null | undefined,
    inventoryOption: SalesOrderInventoryItemOption | null,
  ): string | null {
    if (!inventoryItemId) {
      return null;
    }
    if (inventoryOption?.itemType === INVENTORY_ITEM_TYPE_SERVICE) {
      return 'COMPONENTS.SALES_ORDERS.LINES.SERVICE_BADGE';
    }
    return 'COMPONENTS.SALES_ORDERS.LINES.PRODUCT_BADGE';
  }

  private refreshLineUnitOptions(item: InventoryItem | null): void {
    this.lineUnitOptions$.next(this.buildLineUnitOptions(item));
  }

  private buildLineUnitOptions(item: InventoryItem | null): SalesOrderLineUnitOption[] {
    if (!item) {
      return this.unitsOfMeasure.map((unit) => ({
        unitOfMeasureId: unit.id,
        code: unit.code,
        displayName: unit.displayName,
        qtyPerUnitOfMeasure: 1,
        isBase: false,
      }));
    }

    const baseId = item.baseUnitOfMeasureId ?? null;
    const itemUnits = item.unitsOfMeasure ?? [];
    const lookup = new Map(this.unitsOfMeasure.map((unit) => [unit.id, unit]));

    const options: SalesOrderLineUnitOption[] = itemUnits.map((unit) => ({
      unitOfMeasureId: unit.unitOfMeasureId,
      code: unit.code,
      displayName: unit.displayName,
      qtyPerUnitOfMeasure: unit.qtyPerUnitOfMeasure || 1,
      isBase: baseId !== null && unit.unitOfMeasureId === baseId,
    }));

    if (baseId !== null && !options.some((option) => option.unitOfMeasureId === baseId)) {
      const baseLookup = lookup.get(baseId);
      if (baseLookup) {
        options.unshift({
          unitOfMeasureId: baseLookup.id,
          code: baseLookup.code,
          displayName: baseLookup.displayName,
          qtyPerUnitOfMeasure: 1,
          isBase: true,
        });
      }
    }

    return options;
  }

  private computeLinePreview(): SalesOrderLineTotalsPreview {
    const raw = this.lineForm.getRawValue();
    const quantity = Number.isFinite(raw.quantity) ? Number(raw.quantity) : 0;
    const unitPrice = Number.isFinite(raw.unitPrice) ? Number(raw.unitPrice) : 0;
    const taxPercent = Number.isFinite(raw.taxPercent) ? Number(raw.taxPercent) : 0;

    const selectedUnit = this.lineUnitOptions$.value.find(
      (option) => option.unitOfMeasureId === raw.unitOfMeasureId,
    );

    const factor = selectedUnit?.qtyPerUnitOfMeasure ?? 1;
    const baseUnitOption = this.lineUnitOptions$.value.find((option) => option.isBase);
    const baseUnitOfMeasureName =
      this.lineAvailabilityView$.value?.baseUnitOfMeasureName ?? baseUnitOption?.displayName ?? selectedUnit?.displayName ?? '';

    const amountExcludingTax = quantity * unitPrice;
    const totalTaxAmount = amountExcludingTax * (taxPercent / 100);
    const amountIncludingTax = amountExcludingTax + totalTaxAmount;

    return {
      baseQuantity: quantity * factor,
      baseUnitOfMeasureName,
      amountExcludingTax,
      totalTaxAmount,
      amountIncludingTax,
    };
  }
}
